import {
  IPlatformService,
  Platform,
  TVKeyMap,
  DeviceInfo,
  ScreenOrientation,
  OrientationChangeCallback,
} from '../types';
import { TIZEN_KEYS } from '../keys';
import { generateUUID } from '../../../utils/uuid';

// Tizen FileHandle interface for Filesystem API
interface TizenFileHandle {
  writeBlob(blob: Blob): void;
  writeBlobNonBlocking(
    blob: Blob,
    onSuccess?: (count: number) => void,
    onError?: (error: Error) => void,
  ): void;
  readBlob(size: number): Blob;
  readBlobNonBlocking(
    size: number,
    onSuccess?: (blob: Blob) => void,
    onError?: (error: Error) => void,
  ): void;
  close(): void;
  length: number;
}

declare global {
  interface Window {
    tizen?: {
      application: {
        getCurrentApplication: () => {
          exit: () => void;
        };
      };
      tvinputdevice?: {
        registerKey?: (key: string) => void;
        registerKeyBatch?: (keys: string[]) => void;
      };
      tvaudiocontrol?: {
        setVolume?: (level: number) => void;
        getVolume?: () => number;
        setMute?: (mute: boolean) => void;
        isMute?: () => boolean;
      };
      package?: {
        install(
          packageFileURI: string,
          progressCallback?: (packageId: string, progress: number) => void,
          errorCallback?: (error: Error) => void,
        ): void;
        uninstall(
          packageId: string,
          progressCallback?: (packageId: string, progress: number) => void,
          errorCallback?: (error: Error) => void,
        ): void;
      };
      filesystem?: {
        resolve(
          location: string,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Tizen WebAPI File object (não tipado, varia entre Tizen 4/5/6)
          onSuccess: (dir: any) => void,
          onError?: (error: Error) => void,
          mode?: string,
        ): void;
        openFile(path: string, mode: string): TizenFileHandle;
        createDirectory(
          path: string,
          makeParents: boolean,
          onSuccess?: (path: string) => void,
          onError?: (error: Error) => void,
        ): void;
        deleteFile(
          path: string,
          onSuccess?: () => void,
          onError?: (error: Error) => void,
        ): void;
        deleteDirectory(
          path: string,
          recursive: boolean,
          onSuccess?: () => void,
          onError?: (error: Error) => void,
        ): void;
        pathExists(path: string): boolean;
        isFile(path: string): boolean;
        isDirectory(path: string): boolean;
        toURI(path: string): string;
        listDirectory(
          path: string,
          onSuccess: (names: string[], path: string) => void,
          onError?: (error: Error) => void,
        ): void;
      };
    };
    webapis?: {
      productinfo?: {
        getDuid: () => string;
        getFirmware: () => string;
        getModelCode: () => string;
        getModel: () => string;
        getRealModel: () => string;
      };
      network?: {
        getIp: () => string;
        getWifiSsid: () => string;
        getActiveConnectionType: () => number;
      };
    };
  }
}

/**
 * Atraso do alarme de relaunch, em segundos. Mínimo prático: o alarme do Tizen tem granularidade
 * de segundo, e valores muito baixos podem ser rejeitados pelo firmware. 2s dá margem pro processo
 * encerrar antes de o OS tentar subir de novo — sem isso corre-se o risco de o launch cair no
 * processo que ainda está morrendo. Custo total do ciclo = este atraso + ~4s de boot (medido).
 */
/**
 * Atrasos tentados pro alarme de relaunch, em ordem. O 1º que a plataforma
 * aceitar vale.
 *
 * Era um valor só (2 s). Virou escada porque baixar pra 1 s tem um risco que
 * não dá pra verificar daqui: a Tizen pode recusar `AlarmRelative(1)` — a
 * granularidade mínima do alarme é definida pela implementação e varia por
 * firmware. Com um valor só, uma recusa cairia no `catch` e o app sairia SEM
 * alarme nenhum — que é exatamente o cenário que, nos 5 dias medidos em
 * 2026-07, deixou o app apagado em 6 de 10 mortes (de 15 min a 10 h).
 *
 * Com a escada, o pior caso de tentar 1 s é terminar nos mesmos 2 s de antes.
 */
const RELAUNCH_ALARM_DELAYS_SEC = [1, 2];

export class TizenPlatformService implements IPlatformService {
  readonly platform: Platform = 'tizen';
  readonly keys: TVKeyMap = TIZEN_KEYS;
  private isInitialized = false;
  private orientationCallbacks: Set<OrientationChangeCallback> = new Set();
  private currentOrientation: ScreenOrientation = 'landscape';
  private orientationPollTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Janela de "assentamento" da WebView Tizen (v1.0.216).
   * Após o `init()`, sondamos a orientação a cada {@link ORIENTATION_POLL_INTERVAL_MS}
   * durante {@link ORIENTATION_POLL_WINDOW_MS} pra pegar o momento em que a
   * WebView Tizen Signage acaba de assentar as dimensões finais.
   *
   * <p>Motivação: com {@code screen-orientation="auto-rotation"} (v1.0.215+),
   * o Tizen ajusta {@code window.innerWidth/innerHeight} tardiamente e às vezes
   * SEM disparar {@code resize} (comportamento de firmware). O poll defensivo
   * garante que o React state fique alinhado com o painel físico mesmo quando
   * o listener nativo falha.
   */
  private static readonly ORIENTATION_POLL_INTERVAL_MS = 250;
  private static readonly ORIENTATION_POLL_WINDOW_MS = 5000;

  init(): void {
    if (this.isInitialized) return;

    console.log('[TizenPlatformService] Initializing...');
    this.isInitialized = true;

    this.reportarRelaunchAnterior();

    // Setup orientation antes de tudo — outros consumers (React mount) leem
    // getOrientation em seguida.
    this.setupOrientationListener();

    if (this.isNativeEnvironment()) {
      console.log('[TizenPlatformService] Running on Tizen TV');
      this.registerKeys();
    } else {
      console.log('[TizenPlatformService] Tizen APIs not available (dev mode)');
    }
  }

  /**
   * Registra listener nativo (resize + screen.orientation.change) + poll
   * defensivo de {@link ORIENTATION_POLL_WINDOW_MS} pra cobrir o assentamento
   * tardio da WebView Signage em modo {@code auto-rotation}.
   */
  private setupOrientationListener(): void {
    this.currentOrientation = this.detectOrientation();

    // 1. screen.orientation API — mais preciso quando disponível
    if (typeof screen !== 'undefined' && screen.orientation) {
      try {
        screen.orientation.addEventListener(
          'change',
          this.handleOrientationChange,
        );
      } catch (e) {
        console.warn(
          '[TizenPlatformService] screen.orientation listener failed:',
          e,
        );
      }
    }

    // 2. window resize — cobre a maioria dos firmwares Tizen
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('resize', this.handleResize);
    }

    // 3. Poll defensivo (fecha a race com WebView Tizen — vide comentário do
    //    campo ORIENTATION_POLL_WINDOW_MS). Roda só na janela inicial, depois
    //    para sozinho pra não gastar CPU indefinidamente.
    this.orientationPollTimer = setInterval(() => {
      this.handleOrientationChange();
    }, TizenPlatformService.ORIENTATION_POLL_INTERVAL_MS);
    setTimeout(() => {
      if (this.orientationPollTimer) {
        clearInterval(this.orientationPollTimer);
        this.orientationPollTimer = null;
      }
    }, TizenPlatformService.ORIENTATION_POLL_WINDOW_MS);

    console.log(
      '[TizenPlatformService] Orientation listener ready. Initial:',
      this.currentOrientation,
    );
  }

  private handleOrientationChange = (): void => {
    const detected = this.detectOrientation();
    if (detected !== this.currentOrientation) {
      console.log(
        '[TizenPlatformService] Orientation changed:',
        this.currentOrientation,
        '->',
        detected,
      );
      this.currentOrientation = detected;
      this.notifyOrientationChange(detected);
    }
  };

  private handleResize = (): void => {
    this.handleOrientationChange();
  };

  private detectOrientation(): ScreenOrientation {
    // Prioriza screen.orientation API (mais preciso quando o WebView Tizen
    // decidiu propagar). Fallback usa window.innerWidth/innerHeight — que
    // reflete a viewport CSS atual (não confundir com screen.width, que
    // reporta a resolução física do painel e nem sempre bate com a viewport
    // em WebView).
    if (typeof screen !== 'undefined' && screen.orientation?.type) {
      return screen.orientation.type.includes('portrait')
        ? 'portrait'
        : 'landscape';
    }
    if (typeof window === 'undefined') return 'landscape';
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  }

  private notifyOrientationChange(orientation: ScreenOrientation): void {
    this.orientationCallbacks.forEach((callback) => {
      try {
        callback(orientation);
      } catch (e) {
        console.error(
          '[TizenPlatformService] Error in orientation callback:',
          e,
        );
      }
    });
  }

  private registerKeys(): void {
    // Tizen requires registering certain keys to receive their events
    try {
      const inputDeviceKey = window.tizen?.tvinputdevice;
      if (inputDeviceKey) {
        const keysToRegister = [
          'MediaPlay',
          'MediaPause',
          'MediaStop',
          'MediaRewind',
          'MediaFastForward',
          'ColorF0Red',
          'ColorF1Green',
          'ColorF2Yellow',
          'ColorF3Blue',
        ];
        keysToRegister.forEach((key) => {
          try {
            inputDeviceKey.registerKey?.(key);
          } catch {
            // Key may already be registered or not available
          }
        });
      }
    } catch (error) {
      console.warn('[TizenPlatformService] Failed to register keys:', error);
    }
  }

  exit(): void {
    if (this.isNativeEnvironment()) {
      try {
        window.tizen?.application.getCurrentApplication().exit();
      } catch (error) {
        console.error('[TizenPlatformService] Failed to exit:', error);
      }
    } else {
      console.log(
        '[TizenPlatformService] Exit requested (dev mode - no action)',
      );
    }
  }

  /**
   * Restart remoto (PLAYER_RESTART_PLAN).
   * 'refresh' = window.location.reload() recarrega o WebView interno.
   * 'reboot_device' não tem API estável em Tizen Web App padrão.
   */
  async restart(mode: 'refresh' | 'reboot_device'): Promise<void> {
    if (mode === 'reboot_device') {
      throw new Error(
        'unsupported: reboot_device não disponível em Tizen Web App',
      );
    }
    console.log(
      '[TizenPlatformService] Restart refresh — recarregando WebView',
    );
    window.location.reload();
  }

  onHide(): void {
    console.log('[TizenPlatformService] Application hidden');
  }

  onResume(): void {
    console.log('[TizenPlatformService] Application resumed');
  }

  isNativeEnvironment(): boolean {
    return typeof window !== 'undefined' && !!window.tizen;
  }

  canExit(): boolean {
    return true;
  }

  hasColorButtons(): boolean {
    return true;
  }

  hasMediaKeys(): boolean {
    return true;
  }

  /** TV de verdade: controle remoto, sem toque, 1 decoder de vídeo por hardware. */
  isTV(): boolean {
    return true;
  }

  /**
   * Returns the Samsung DUID (Device Unique ID).
   * This is a hardware-based ID that persists across reinstalls.
   */
  async getHardwareId(): Promise<string> {
    if (!this.isNativeEnvironment()) {
      // In dev mode without Tizen APIs, generate a persistent dev ID
      let devId = localStorage.getItem('dev_tizen_hardware_id');
      if (!devId) {
        devId = `DEV-TIZEN-${generateUUID().substring(0, 8).toUpperCase()}`;
        localStorage.setItem('dev_tizen_hardware_id', devId);
      }
      console.log(
        '[TizenPlatformService] Dev mode - using generated ID:',
        devId,
      );
      return devId;
    }

    // Camada 1: webapis.productinfo.getDuid — Samsung DUID (fonte oficial).
    // Se disponível, retorna imediatamente.
    // Timeout defensivo: em algumas TVs Signage o Runtime pode fazer hang
    // na primeira chamada (esperando o carregamento tardio do webapis.js).
    // 3s cobre 99% dos casos; após isso cai no fallback (Camada 2).
    try {
      const duid = await this.readDuidWithTimeout(3000);
      if (duid) {
        const hardwareId = `TIZEN-${duid}`;
        console.log(
          '[TizenPlatformService] Hardware ID obtained (DUID):',
          hardwareId.substring(0, 20) + '...',
        );
        return hardwareId;
      }
    } catch (error) {
      console.warn(
        '[TizenPlatformService] DUID unavailable, falling back to persisted UUID:',
        error,
      );
    }

    // Camada 2: UUID persistido em localStorage — fallback "nunca throws".
    // Prefixo TIZEN-FB- distingue do DUID real (TIZEN-...) e do dev
    // (DEV-TIZEN-...). Persiste entre reinicializações do app; se um dia
    // o webapis passar a funcionar, `verifyAndUpdateHardwareId` no
    // auth.store migra pro DUID real automaticamente.
    return this.getOrCreatePersistedFallbackId();
  }

  /**
   * Chama webapis.productinfo.getDuid dentro de um timeout defensivo.
   * getDuid oficialmente é síncrono, mas envolvemos em Promise pra
   * capturar hangs raros observados em firmware Tizen 6.5 antigo
   * (chamada nunca retorna nem lança).
   */
  private readDuidWithTimeout(timeoutMs: number): Promise<string | null> {
    return new Promise((resolve, reject) => {
      if (!window.webapis?.productinfo?.getDuid) {
        resolve(null);
        return;
      }
      const timer = setTimeout(() => {
        reject(new Error(`getDuid timed out after ${timeoutMs}ms`));
      }, timeoutMs);
      try {
        const duid = window.webapis.productinfo.getDuid();
        clearTimeout(timer);
        resolve(typeof duid === 'string' && duid.length > 0 ? duid : null);
      } catch (err) {
        clearTimeout(timer);
        reject(err);
      }
    });
  }

  private getOrCreatePersistedFallbackId(): string {
    const KEY = 'tizen_fallback_hardware_id';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `TIZEN-FB-${generateUUID().substring(0, 8).toUpperCase()}`;
      localStorage.setItem(KEY, id);
      console.warn(
        '[TizenPlatformService] Generated fallback hardware ID:',
        id,
      );
    } else {
      console.log(
        '[TizenPlatformService] Reusing persisted fallback hardware ID:',
        id,
      );
    }
    return id;
  }

  /**
   * Versão da plataforma Tizen ("6.5", "9.0") via `tizen.systeminfo`.
   *
   * `getCapability` é SÍNCRONO (diferente do `getPropertyValue`) e LANÇA quando
   * a chave não existe — daí o try/catch. Acesso direto ao `window.tizen` é
   * permitido aqui: este arquivo É a implementação de plataforma (vide
   * PLATFORM-ARCHITECTURE.md).
   *
   * Retorna `null` em qualquer falha (TV antiga sem a capability, dev mode,
   * API ausente) — o caller cai no firmware.
   */
  private getTizenPlatformVersion(): string | null {
    try {
      const systeminfo = (
        window as unknown as {
          tizen?: {
            systeminfo?: { getCapability?: (key: string) => unknown };
          };
        }
      ).tizen?.systeminfo;
      const raw = systeminfo?.getCapability?.(
        'http://tizen.org/feature/platform.version',
      );
      if (typeof raw !== 'string') return null;
      const version = raw.trim();
      return version.length > 0 ? version : null;
    } catch (err) {
      console.warn(
        '[TizenPlatformService] platform.version indisponível:',
        err,
      );
      return null;
    }
  }

  /**
   * Collects device information from Samsung Tizen APIs.
   */
  /**
   * `tizen.systeminfo` expõe a RAM do sistema de forma síncrona e barata.
   */
  async getSystemMemoryMb(): Promise<{
    freeMb: number;
    totalMb: number | null;
  } | null> {
    try {
      const si = (
        window as unknown as {
          tizen?: {
            systeminfo?: {
              getAvailableMemory?: () => number;
              getTotalMemory?: () => number;
            };
          };
        }
      ).tizen?.systeminfo;
      const bytes = si?.getAvailableMemory?.();
      if (typeof bytes !== 'number' || bytes <= 0) return null;
      const total = si?.getTotalMemory?.();
      return {
        freeMb: Math.round(bytes / 1048576),
        totalMb:
          typeof total === 'number' && total > 0
            ? Math.round(total / 1048576)
            : null,
      };
    } catch {
      return null;
    }
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    const info: DeviceInfo = {
      platform: 'tizen',
      manufacturer: 'Samsung',
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      orientation: this.getOrientation(),
    };

    if (!this.isNativeEnvironment()) {
      console.log(
        '[TizenPlatformService] Dev mode - returning basic device info',
      );
      info.model = 'Development Mode';
      info.platformVersion = 'N/A';
      return info;
    }

    try {
      // Product info
      if (window.webapis?.productinfo) {
        const productInfo = window.webapis.productinfo;
        // Preferir realModel (legível: "UN50U8600FGXZD") sobre modelCode
        // ("25_KSUE_UB_VOX") quando disponível.
        const realModel = productInfo.getRealModel?.();
        info.model =
          realModel ||
          productInfo.getModelCode?.() ||
          productInfo.getModel?.() ||
          'Unknown';
        // Versão do SO reportada ao servidor. Preferimos a versão da PLATAFORMA
        // Tizen ("6.5", "9.0") sobre o firmware Samsung
        // ("T-KSU2EFUABC-0090-1300.9"): é o que o admin mostra na coluna
        // "Versão SO", e o firmware não diz qual Tizen a TV roda. O firmware
        // continua indo separado em `extra.firmwareVersion` (abaixo), então
        // nada se perde.
        info.platformVersion =
          this.getTizenPlatformVersion() ||
          productInfo.getFirmware?.() ||
          'Unknown';

        // Hardware identifiers em extra → backend grava em metadata JSONB.
        // DUID é o ID hardware único Samsung Tizen (16 chars hex, imutável).
        // Usado pra: rastreio individual, cadastro em Distributor Cert,
        // troubleshooting em campo.
        const extra: Record<string, unknown> = {};
        try {
          if (productInfo.getDuid) {
            extra.duid = productInfo.getDuid();
          }
        } catch (err) {
          console.warn('[TizenPlatformService] getDuid failed:', err);
        }
        if (realModel) extra.realModel = realModel;
        const modelCode = productInfo.getModelCode?.();
        if (modelCode) extra.modelCode = modelCode;
        const firmware = productInfo.getFirmware?.();
        if (firmware) extra.firmwareVersion = firmware;
        info.extra = extra;
      }

      // Network info
      if (window.webapis?.network) {
        const network = window.webapis.network;
        console.log(
          '[TizenPlatformService] Network API available, fetching info...',
        );

        try {
          info.ipAddress = network.getIp?.();
          console.log('[TizenPlatformService] IP Address:', info.ipAddress);
        } catch (err) {
          console.warn('[TizenPlatformService] Could not get IP address:', err);
        }

        try {
          info.networkName = network.getWifiSsid?.();
          console.log('[TizenPlatformService] WiFi SSID:', info.networkName);
        } catch (err) {
          console.warn('[TizenPlatformService] Could not get WiFi SSID:', err);
        }

        try {
          const connType = network.getActiveConnectionType?.();
          console.log('[TizenPlatformService] Connection type:', connType);
          info.extra = { ...info.extra, connectionType: connType };
        } catch (err) {
          console.warn(
            '[TizenPlatformService] Could not get connection type:',
            err,
          );
        }
      } else {
        console.warn('[TizenPlatformService] Network API not available');
      }
    } catch (error) {
      console.error(
        '[TizenPlatformService] Error collecting device info:',
        error,
      );
    }

    console.log(
      '[TizenPlatformService] Device info collected:',
      JSON.stringify(info, null, 2),
    );
    return info;
  }

  // Orientation methods

  /**
   * TVs Samsung convencionais não suportam mudança de orientação em runtime.
   * Apenas o Samsung Sero (com rotator) suporta, mas é raro.
   */
  /**
   * v1.0.216+: Tizen respeita mudanças de orientação (auto-rotation habilitado
   * em v1.0.215). Antes retornava {@code false} porque o WGT era landscape-fixo.
   */
  supportsOrientationChange(): boolean {
    return true;
  }

  /**
   * Detecta orientação atual. Reporta o valor "vivo" quando disponível
   * (após {@link init} + listeners registrados) — evita a race de mount
   * onde a WebView Tizen ainda não assentou.
   */
  getOrientation(): ScreenOrientation {
    return this.isInitialized
      ? this.currentOrientation
      : this.detectOrientation();
  }

  /**
   * Registra callback para mudanças de orientação. Dispara quando:
   * - {@code screen.orientation.change} evento nativo (Tizen 6+)
   * - {@code window.resize} (fallback)
   * - Poll de 250ms nos primeiros {@code ORIENTATION_POLL_WINDOW_MS} pra
   *   cobrir o "assentamento" tardio da WebView Signage
   */
  onOrientationChange(callback: OrientationChangeCallback): () => void {
    this.orientationCallbacks.add(callback);
    return () => this.orientationCallbacks.delete(callback);
  }

  /**
   * Reads physical panel brightness on Tizen B2B (Signage SDK). On consumer
   * Samsung TVs this always returns null — Samsung does not expose backlight
   * to the app; user must change brightness via TV menu/remote.
   *
   * <p>API: {@code b2bapis.brightness.getBrightness()} returns 0-100. Method
   * is only present when the B2B SDK bundle is included in {@code config.xml}.
   */
  async getHardwareBrightness(): Promise<number | null> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      const api = w?.b2bapis?.brightness;
      if (api && typeof api.getBrightness === 'function') {
        const value = api.getBrightness();
        if (typeof value === 'number' && value >= 0 && value <= 100) {
          return Math.round(value);
        }
      }
      return null; // consumer Tizen sem B2B SDK
    } catch (e) {
      console.warn('[TizenPlatformService] getHardwareBrightness failed:', e);
      return null;
    }
  }

  /**
   * Writes physical brightness on Tizen B2B (Signage SDK). On consumer
   * Samsung TVs throws 'unsupported' — Samsung does not allow apps to
   * change brightness.
   */
  async setHardwareBrightness(level: number): Promise<void> {
    const safeLevel = Math.max(0, Math.min(100, Math.round(level)));
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      const api = w?.b2bapis?.brightness;
      if (api && typeof api.setBrightness === 'function') {
        api.setBrightness(safeLevel);
        return;
      }
    } catch (e) {
      console.warn('[TizenPlatformService] setHardwareBrightness failed:', e);
    }
    const err = new Error(
      'Hardware brightness unsupported on Tizen consumer (B2B SDK not loaded)',
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (err as any).code = 'unsupported';
    throw err;
  }

  // OTA Update methods

  /**
   * Install an OTA update bundle using Tizen package API.
   * @param bundlePath Local file path to the downloaded bundle (.wgt file)
   * @returns Promise that resolves when installation is complete
   * @throws Error if installation fails or platform API is not available
   */
  async installUpdate(bundlePath: string): Promise<void> {
    console.log('[TizenPlatformService] Installing update:', bundlePath);

    if (!this.isNativeEnvironment()) {
      console.warn(
        '[TizenPlatformService] Cannot install update in dev mode (Tizen API not available)',
      );
      throw new Error('OTA installation is only supported on Tizen devices');
    }

    if (!window.tizen?.package) {
      console.error('[TizenPlatformService] Tizen package API not available');
      throw new Error('Tizen package API is not available on this device');
    }

    if (!window.tizen?.filesystem) {
      console.error(
        '[TizenPlatformService] Tizen filesystem API not available',
      );
      throw new Error('Tizen filesystem API is not available on this device');
    }

    return new Promise<void>((resolve, reject) => {
      try {
        const filesystem = window.tizen!.filesystem!;
        const packageApi = window.tizen!.package!;

        // Verify bundle exists before attempting installation
        if (!filesystem.pathExists(bundlePath)) {
          const error = `Bundle file not found: ${bundlePath}`;
          console.error('[TizenPlatformService]', error);
          reject(new Error(error));
          return;
        }

        if (!filesystem.isFile(bundlePath)) {
          const error = `Path is not a file: ${bundlePath}`;
          console.error('[TizenPlatformService]', error);
          reject(new Error(error));
          return;
        }

        // Convert file path to URI format required by package API
        const packageURI = filesystem.toURI(bundlePath);
        console.log('[TizenPlatformService] Bundle URI:', packageURI);

        // Install the package
        packageApi.install(
          packageURI,
          (packageId: string, progress: number) => {
            console.log(
              `[TizenPlatformService] Installation progress: ${packageId} - ${progress}%`,
            );
            if (progress === 100) {
              console.log(
                '[TizenPlatformService] Installation completed successfully',
              );
              resolve();
            }
          },
          (error: Error) => {
            console.error('[TizenPlatformService] Installation failed:', error);
            reject(
              new Error(
                `Failed to install update: ${error.message || 'Unknown error'}`,
              ),
            );
          },
        );
      } catch (error) {
        console.error(
          '[TizenPlatformService] Error during installation:',
          error,
        );
        reject(
          new Error(
            `Installation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          ),
        );
      }
    });
  }

  /**
   * Restart the application after update.
   * On Tizen, this exits the app. The system will relaunch the updated version.
   */
  restartApp(): void {
    console.log('[TizenPlatformService] Restarting app after update...');

    if (!this.isNativeEnvironment()) {
      console.warn(
        '[TizenPlatformService] Dev mode - simulating restart (reload)',
      );
      window.location.reload();
      return;
    }

    this.agendarRelaunch();

    try {
      // Exit the app — com o alarme agendado, quem relança é o OS; sem ele, resta o launcher
      // (que nos dados de 5 dias falhou em 6 de 10 mortes). NÃO recarregar depois daqui: o
      // caminho de OTA depende do exit pra carregar a versão nova.
      window.tizen?.application.getCurrentApplication().exit();
    } catch (error) {
      console.error('[TizenPlatformService] Failed to restart app:', error);
      // Fallback to reload
      window.location.reload();
    }
  }

  /**
   * Conta, no boot, como foi o último restart — e é a ÚNICA forma de saber.
   *
   * <p>O log do `agendarRelaunch` sai milissegundos antes do `exit()`, então
   * some junto com o processo: depois do restart ninguém consegue dizer se o
   * alarme pegou nem com qual atraso. O marcador em localStorage sobrevive ao
   * exit; este método o lê uma vez e o joga no log, onde o `logs:tail` do admin
   * enxerga.</p>
   *
   * <p>É por aqui que se responde "a TV aceitou 1 s?" sem precisar de devtools
   * na loja — pergunta aberta desde que o atraso virou escada (1 s → 2 s).</p>
   */
  private reportarRelaunchAnterior(): void {
    try {
      const bruto = window.localStorage?.getItem('efflux_relaunch_alarm_v1');
      if (!bruto) return;
      const m = JSON.parse(bruto) as {
        at?: number;
        scheduled?: boolean;
        delaySec?: number | null;
      };
      const haQuantoTempo = m.at ? Math.round((Date.now() - m.at) / 1000) : null;
      if (m.scheduled) {
        console.log(
          `[TizenPlatformService] voltei de um restart: alarme de ${m.delaySec}s ` +
            `pegou (saí há ${haQuantoTempo}s)`,
        );
      } else {
        console.warn(
          `[TizenPlatformService] voltei de um restart SEM alarme — a TV recusou ` +
            `todos os atrasos (saí há ${haQuantoTempo}s). Quem me trouxe foi o launcher.`,
        );
      }
      window.localStorage?.removeItem('efflux_relaunch_alarm_v1');
    } catch {
      /* best-effort — nunca atrapalhar o boot */
    }
  }

  /**
   * Agenda o relaunch no OS ANTES de sair (2026-07-28) — quem traz o app de
   * volta passa a ser o sistema, não o launcher.
   *
   * <p>O `exit()` puro APOSTA que o Tizen relança. Nos 5 dias medidos na SSSP
   * essa aposta perdeu em 6 de 10 mortes: o app ficou apagado de 15 min a 10 h.
   * Um `AlarmRelative` registrado antes do exit sobrevive ao fim do processo.</p>
   *
   * <p>Tenta os atrasos de {@link RELAUNCH_ALARM_DELAYS_SEC} em ordem: o 1º que
   * a plataforma aceitar vale. Se a TV recusar o mais curto (a granularidade
   * mínima do alarme varia por firmware), cai no seguinte em vez de ficar sem
   * alarme nenhum.</p>
   *
   * <p>Requer `http://tizen.org/privilege/alarm` no config.xml. Sem ele a API
   * existe mas `alarm.add` lança SecurityError.</p>
   *
   * @returns o atraso que a plataforma aceitou, ou null se nenhum passou.
   */
  private agendarRelaunch(): number | null {
    let aceito: number | null = null;
    let ultimoErro: unknown = null;

    try {
      const t = window.tizen as unknown as {
        application?: {
          getCurrentApplication?: () => { appInfo?: { id?: string } };
        };
        alarm?: { add?: (a: unknown, id: string) => void };
        AlarmRelative?: new (delaySec: number) => unknown;
      };
      const appId = t?.application?.getCurrentApplication?.()?.appInfo?.id;
      if (appId && t?.alarm?.add && t?.AlarmRelative) {
        for (const delay of RELAUNCH_ALARM_DELAYS_SEC) {
          try {
            t.alarm.add(new t.AlarmRelative(delay), appId);
            aceito = delay;
            console.log(
              `[TizenPlatformService] relaunch agendado em ${delay}s (alarm)`,
            );
            break;
          } catch (error) {
            ultimoErro = error;
            console.warn(
              `[TizenPlatformService] a TV recusou AlarmRelative(${delay}s):`,
              error,
            );
          }
        }
      }
    } catch (error) {
      ultimoErro = error;
    }

    if (aceito === null && ultimoErro) {
      console.warn('[TizenPlatformService] alarm indisponível:', ultimoErro);
    }

    // Marcador pra VERIFICAR no device se o alarme realmente pegou (o privilege pode ser negado
    // pelo cert/firmware sem erro visível). Sobrevive ao exit — é o que diz, depois do restart, se
    // quem trouxe o app de volta foi o OS ou o launcher, e com qual atraso.
    try {
      window.localStorage?.setItem(
        'efflux_relaunch_alarm_v1',
        JSON.stringify({ at: Date.now(), scheduled: aceito !== null, delaySec: aceito }),
      );
    } catch {
      /* best-effort */
    }

    return aceito;
  }

  /**
   * Reinicia o PROCESSO — alarme + `exit()`, como o {@link restartApp}, com uma
   * diferença que importa: <b>se nenhum alarme for aceito, NÃO sai.</b>
   *
   * <p>Os dois métodos divergem porque o que está em jogo é diferente. O
   * `restartApp` serve ao OTA: lá sair é obrigatório, porque é o exit que
   * carrega a versão nova — sem alarme ele ainda assim sai, apostando no
   * launcher. Aqui, no botão do admin, sair sem alarme não compra nada: a TV
   * tem 60% de chance histórica de ficar apagada, e o que se queria era só
   * devolver memória. Recarregar não devolve memória nativa, mas deixa a loja
   * tocando — é a troca certa.</p>
   *
   * <p>Mesmo princípio do Android, onde o `AppRestarter` se recusa a matar o
   * processo se não conseguir garantir a volta.</p>
   *
   * <p>Existe como método próprio porque o contrato dos dois é diferente:
   * `restartApp` é do fluxo de OTA e varia por plataforma (no Android ele
   * precisa recarregar, não matar). Expor `restartProcess` deixa quem chama
   * perguntar "esta plataforma sabe trocar de processo?" sem ter que conhecer
   * a lista de plataformas.</p>
   *
   * @since 1.0.453
   */
  restartProcess(): void {
    if (!this.isNativeEnvironment()) {
      window.location.reload();
      return;
    }

    const delay = this.agendarRelaunch();
    if (delay === null) {
      console.warn(
        '[TizenPlatformService] nenhum alarme aceito — NÃO vou sair. ' +
          'Recarregando: não devolve memória nativa, mas a TV continua tocando.',
      );
      window.location.reload();
      return;
    }

    try {
      window.tizen?.application.getCurrentApplication().exit();
    } catch (error) {
      console.error('[TizenPlatformService] exit falhou:', error);
      window.location.reload();
    }
  }

  /**
   * Get temporary directory for OTA downloads.
   * On Tizen, this returns the downloads directory path.
   * @returns Path to temp directory
   */
  getTempDirectory(): string {
    if (!this.isNativeEnvironment()) {
      console.log('[TizenPlatformService] Dev mode - using browser temp path');
      return '/tmp';
    }

    // Tizen uses virtual root paths
    // 'downloads' is a common location for temporary files
    return 'downloads';
  }

  // Fullscreen methods (no-op for TVs - always fullscreen)

  /**
   * Tizen TVs don't support fullscreen toggle - they always run fullscreen.
   */
  supportsFullscreen(): boolean {
    return false;
  }

  /**
   * Tizen TVs are always in fullscreen mode.
   */
  isFullscreen(): boolean {
    return true;
  }

  /**
   * No-op on Tizen TVs - already fullscreen.
   */
  async enterFullscreen(): Promise<void> {
    // No-op - Tizen apps always run fullscreen
  }

  /**
   * No-op on Tizen TVs - cannot exit fullscreen.
   */
  async exitFullscreen(): Promise<void> {
    // No-op - Tizen apps always run fullscreen
  }

  /**
   * No-op on Tizen TVs - fullscreen state never changes.
   * Returns a cleanup function that does nothing.
   */
  onFullscreenChange(): () => void {
    // No-op - Tizen apps are always fullscreen
    return () => {};
  }

  // Screen Wake Lock (V058) — no-op: a TV Tizen mantém a tela acesa
  // nativamente (signage 24/7). A flag keepScreenAwake é de browser.
  supportsWakeLock(): boolean {
    return false;
  }

  async requestScreenWakeLock(): Promise<void> {
    // no-op (tela acesa nativa)
  }

  async releaseScreenWakeLock(): Promise<void> {
    // no-op
  }
}
